import React, { useEffect, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import './Approvisionnement.css';
import Card15 from '../components/Card15';
import Card16 from '../components/Card16';
import Card17 from '../components/Card17';
import Card18 from '../components/Card18';
import Card19 from '../components/Card19';
import Card20 from '../components/Card20';
import Card21 from '../components/Card21';
import Camembert3 from '../components/Camembert3';
import Graph2 from '../components/Graph2';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, DataLabelsPlugin);
const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://myssa1.osc-fr1.scalingo.io/api';
// ★ RÉGLAGES LARGEURS COLONNES — table20 (analyse_delai_approvisionnement)
// Modifie uniquement les valeurs px selon tes besoins
const COLUMN_WIDTHS_TABLE20 = {
  'Item':                                    '180px',
  'Description':                             '260px',
  'Délai appro actuel (jours ouv.)':         '120px',
  'Délais proposé':                          '85px',
  'Ecart':                                    '70px',
  'Evolution':                                '100px',
  'Qté liv 10 dernière commande':            '120px',
  'Délai moyen réel (jours ouv.)':           '75px',
   'Délais moyen':           '75px',
   'Délais mini':           '75px',
   'Délais max':           '75px',
      'Délais systèmes':           '90px',
        'Délais réel corrigé':           '90px',
    'C':           '55px',
        'C-1':           '55px',
            'C-2':           '55px',
                'C-3':           '55px',
                    'C-4':           '55px',
                        'C-5':           '55px',
                            'C-6':           '55px',
                                'C-7':           '55px',
                                    'C-8':           '55px',
                                        'C-9':           '55px',
};
// Toute colonne absente du mapping recevra cette largeur par défaut :
const TABLE20_DEFAULT_WIDTH = '110px';
// ★ RÉGLAGES — taille de la modal de détail article (fiche article)
// Modifie uniquement ces deux valeurs pour agrandir/réduire la modal
const MODAL_CONFIG = {
  maxWidth:   '1500px', // largeur max de la modal
  maxHeightVh: 92,      // hauteur max, en % de la hauteur de fenêtre
};
// ★ RÉGLAGES — design table2 (recalage commande frn — stock fort)
// Palette dérivée de l'identité MYSSA (navy / blue / cyan)
const T2_THEME = {
  navy:       '#0D2140',
  blue:       '#1A5FA8',
  cyan:       '#2BAADB',
  surface:    '#FFFFFF',
  canvas:     '#F6F9FC',
  border:     '#E3E9F0',
  textMain:   '#16243B',
  textSoft:   '#64748B',
  retard:     { fg: '#C0392B', bg: '#FCEDEB' },
  j:          { fg: '#B7770D', bg: '#FFF8E6' },
  ok:         { fg: '#1E8E5A', bg: '#EAF8F1' },
};
const getT2StatusMeta = (value) => {
  if (value === 'Retard') return T2_THEME.retard;
  if (value === 'J')      return T2_THEME.j;
  return T2_THEME.ok;
};
// ★ Style commun "pastille de statut" — réutilisé sur tous les tableaux
// pour garder un seul et même langage visuel (au lieu d'aplats de fond pleine cellule/ligne)
const t2PillStyle = (meta) => ({
  display: 'inline-block',
  padding: '2px 9px',
  borderRadius: '999px',
  fontSize: '11.5px',
  fontWeight: 600,
  color: meta.fg,
  background: meta.bg,
  whiteSpace: 'nowrap',
});
// ★ Style bouton "Réinitialiser" — sobre, cohérent avec le reste de la page (pas de dégradé)
const t2ResetBtnStyle = {
  background: '#FFFFFF',
  color: T2_THEME.navy,
  border: `1px solid ${T2_THEME.border}`,
  padding: '6px 14px',
  borderRadius: '7px',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer',
};
// ──────────────────────────────────────────────────────────────────────────
// ★ Graphe stock projeté + simulation recalage — repris de Stockprojete.jsx
// pour garder le même calcul entre les deux pages.
// ──────────────────────────────────────────────────────────────────────────
const fmtQty = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));
const parseLocalDate = (str) => {
  if (!str) return null;
  const parts = String(str).split('/');
  if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
  return new Date(str);
};
const getMonthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const getWeekKey = (d) => {
  const tmp = new Date(d); tmp.setHours(0, 0, 0, 0);
  const day = tmp.getDay() || 7; tmp.setDate(tmp.getDate() - day + 1);
  return `${tmp.getFullYear()}-S${String(Math.ceil(
    ((tmp - new Date(tmp.getFullYear(), 0, 1)) / 86400000 + 1) / 7
  )).padStart(2, '00')}`;
};
const aggregateRows = (rows, granularity) => {
  if (granularity === 'jour') return rows.map(r => ({ ...r, _periodKey: r.date }));
  const groups = {};
  rows.forEach((r) => {
    const d = parseLocalDate(r.date);
    if (!d || isNaN(d)) return;
    const key = granularity === 'semaine' ? getWeekKey(d) : getMonthKey(d);
    if (!groups[key]) groups[key] = { lastRow: null, sumDepart: 0, sumLivraison: 0, sumPrevision: 0, sumCommandeFerme: 0 };
    const g = groups[key];
    if (!g.lastRow || parseLocalDate(r.date) >= parseLocalDate(g.lastRow.date)) g.lastRow = r;
    const lf = parseFloat(r['livraison fournisseur']); if (!isNaN(lf)) g.sumLivraison += lf;
    const dc = parseFloat(r['depart client']);
    if (!isNaN(dc)) {
      g.sumDepart += dc;
      if (r['type de donnee'] === 'prévision') g.sumPrevision += dc;
      if (r['type de donnee'] === 'commande ferme') g.sumCommandeFerme += dc;
    }
  });
  return Object.entries(groups).filter(([, g]) => g.lastRow).map(([key, g]) => ({
    ...g.lastRow,
    'depart client': g.sumDepart,
    'livraison fournisseur': g.sumLivraison,
    _sumPrevision: g.sumPrevision,
    _sumCommandeFerme: g.sumCommandeFerme,
    _periodKey: key,
  })).sort((a, b) => (parseLocalDate(a.date) || 0) - (parseLocalDate(b.date) || 0));
};
const formatGraphLabel = (dateStr, granularity) => {
  const d = parseLocalDate(dateStr);
  if (!d || isNaN(d)) return dateStr || '';
  if (granularity === 'jour') return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  if (granularity === 'semaine') return `S${String(Math.ceil(
    ((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7
  )).padStart(2, '0')} ${d.getFullYear()}`;
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};
const buildChartData = (rows, granularity = 'mois') => {
  const agg = aggregateRows(rows, granularity);
  return {
    labels: agg.map((r) => formatGraphLabel(r.date, granularity)),
    datasets: [
      {
        label: 'Stock projeté', type: 'line',
        data: agg.map((r) => Math.round(parseFloat(r['stock projete']) || 0)),
        borderColor: T2_THEME.navy, backgroundColor: 'rgba(13,33,64,0.15)',
        borderWidth: 2.5, fill: false, tension: 0.3, datalabels: { display: false },
      },
      {
        label: 'Stock de sécurité', type: 'line',
        data: new Array(agg.length).fill(Math.round(parseFloat(agg[0]?.['stock mini'] || 0))),
        borderColor: T2_THEME.retard.fg, backgroundColor: 'rgba(192,57,43,0.08)',
        borderWidth: 1.5, fill: false, borderDash: [6, 4], datalabels: { display: false },
      },
      {
        label: 'Prévision', type: 'bar',
        data: agg.map((r) => granularity === 'jour'
          ? (r['type de donnee'] === 'prévision' ? Math.round(parseFloat(r['depart client']) || 0) : 0)
          : Math.round(r._sumPrevision || 0)),
        backgroundColor: 'rgba(26,95,168,0.45)', borderColor: T2_THEME.blue,
        borderWidth: 1, stack: 'out', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: T2_THEME.blue, anchor: 'end', align: 'top', font: { size: 10 } },
      },
      {
        label: 'Commande ferme', type: 'bar',
        data: agg.map((r) => granularity === 'jour'
          ? (r['type de donnee'] === 'commande ferme' ? Math.round(parseFloat(r['depart client']) || 0) : 0)
          : Math.round(r._sumCommandeFerme || 0)),
        backgroundColor: 'rgba(192,57,43,0.45)', borderColor: T2_THEME.retard.fg,
        borderWidth: 1, stack: 'out', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: T2_THEME.retard.fg, anchor: 'end', align: 'top', font: { size: 10 } },
      },
      {
        label: 'Livraison fournisseur', type: 'bar',
        data: agg.map((r) => Math.round(parseFloat(r['livraison fournisseur']) || 0)),
        backgroundColor: 'rgba(183,119,13,0.5)', borderColor: T2_THEME.j.fg,
        borderWidth: 1, stack: 'in', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: T2_THEME.j.fg, anchor: 'end', align: 'top', font: { size: 10 } },
      },
    ],
  };
};
const chartOptions = {
  responsive: true, maintainAspectRatio: false,
  scales: {
    x: { stacked: false, ticks: { maxRotation: 45, minRotation: 0, font: { size: 11 } }, grid: { color: '#F3F4F6' } },
    y: { stacked: false, ticks: { font: { size: 11 } }, grid: { color: '#F3F4F6' } },
  },
  plugins: {
    legend: { display: true, labels: { font: { size: 12 }, boxWidth: 12 } },
    datalabels: { color: '#374151', font: { weight: 'bold', size: 10 } },
  },
};
// Applique reports + annulations de recalage_cmde_frn sur les lignes brutes et
// recalcule le stock projeté chronologiquement — fonction au niveau module,
// identique à Stockprojete.jsx, pour la courbe verte en pointillés du graphe.
const buildSimulatedRows = (rows, cmds) => {
  let simRows = rows.map(r => ({ ...r }));
  cmds.forEach(cmd => {
    const origDate   = cmd['Date de livraison'];
    const reportDate = cmd['Report proposé'];
    const statut     = (cmd.statut || '').trim().toUpperCase();
    const qty        = parseFloat(cmd['Quantité commandée']) || 0;
    if (statut === 'ANNULATION') {
      simRows = simRows.map(r => {
        const rDate = parseLocalDate(r.date);
        const oDate = parseLocalDate(origDate) || new Date(origDate);
        if (!rDate || !oDate || isNaN(rDate) || isNaN(oDate)) return r;
        if (rDate.toDateString() === oDate.toDateString() && r['type de donnee'] === 'commande frn') {
          return { ...r, 'livraison fournisseur': Math.max(0, (parseFloat(r['livraison fournisseur']) || 0) - qty) };
        }
        return r;
      });
    } else if (reportDate && reportDate !== 'ANNULATION') {
      const oDate = parseLocalDate(origDate) || new Date(origDate);
      const nDate = parseLocalDate(reportDate) || new Date(reportDate);
      if (!oDate || !nDate || isNaN(oDate) || isNaN(nDate)) return;
      simRows = simRows.map(r => {
        const rDate = parseLocalDate(r.date);
        if (!rDate || isNaN(rDate)) return r;
        if (rDate.toDateString() === oDate.toDateString() && r['type de donnee'] === 'commande frn') {
          return { ...r, 'livraison fournisseur': Math.max(0, (parseFloat(r['livraison fournisseur']) || 0) - qty) };
        }
        return r;
      });
      const existingIdx = simRows.findIndex(r => {
        const rDate = parseLocalDate(r.date);
        return rDate && !isNaN(rDate) && rDate.toDateString() === nDate.toDateString();
      });
      if (existingIdx >= 0) {
        simRows[existingIdx] = {
          ...simRows[existingIdx],
          'livraison fournisseur': (parseFloat(simRows[existingIdx]['livraison fournisseur']) || 0) + qty,
        };
      } else {
        const refRow = simRows[0] || {};
        const dd = nDate.getDate(), mm = nDate.getMonth() + 1, yyyy = nDate.getFullYear();
        simRows.push({
          ...refRow,
          date: `${String(dd).padStart(2,'0')}/${String(mm).padStart(2,'0')}/${yyyy}`,
          'type de donnee': 'commande frn',
          'livraison fournisseur': qty,
          'depart client': 0,
        });
      }
    }
  });
  simRows.sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
  let stockCourant = parseFloat(simRows[0]?.stock) || 0;
  simRows = simRows.map((r) => {
    const lf = parseFloat(r['livraison fournisseur']) || 0;
    const dc = parseFloat(r['depart client']) || 0;
    stockCourant = stockCourant + lf - dc;
    return { ...r, 'stock projete': stockCourant };
  });
  return simRows;
};
// Libellé Avancement / Report / Annulation / Inchangé — couleurs IDENTIQUES à
// celles utilisées dans la modale de Stockprojete.jsx (même format demandé).
const getStockprojeteStatutLabel = (c) => {
  const statut = (c.statut || '').trim().toUpperCase();
  if (statut === 'ANNULATION') return { label: 'Annulation', bg: '#FEE2E2', color: '#B91C1C' };
  const origD   = parseLocalDate(c['Date de livraison']) || new Date(c['Date de livraison']);
  const reportD = parseLocalDate(c['Report proposé'])    || new Date(c['Report proposé']);
  if (!origD || !reportD || isNaN(origD) || isNaN(reportD)) {
    return { label: c.statut || '—', bg: '#E8F4FD', color: '#1565C0' };
  }
  const diffJours = Math.round((reportD - origD) / (1000 * 60 * 60 * 24));
  if (diffJours < 0) return { label: `Avancement (${Math.abs(diffJours)}j)`, bg: '#EAF3DE', color: '#3B6D11' };
  if (diffJours > 0) return { label: `Report (${diffJours}j)`, bg: '#FFF3CD', color: '#856404' };
  return { label: 'Inchangé', bg: '#F3F4F6', color: '#6B7280' };
};
const Approvisionnement = () => {
  
  const [data, setData] = useState([]);
  const [activeTable, setActiveTable] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [showCharts, setShowCharts] = useState(true);
  const [histogramData, setHistogramData] = useState([]);
  const [simuCmdeFrnValues, setSimuCmdeFrnValues] = useState({});
  const [projectedStockValues, setProjectedStockValues] = useState([]);
  const [stockMiniValue, setStockMiniValue] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [recalageCmds, setRecalageCmds] = useState([]);
  const [itemClickError, setItemClickError] = useState(null);
  const [granularity, setGranularity] = useState('mois');
  
    useEffect(() => {                                    // ← ajouter ici
    document.title = 'MYSSA – Approvisionnement';
  }, []);
  // ================= FORMAT DATE jj/mm/aaaa =================
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(String(dateValue))) return dateValue;
    const dateObj = new Date(dateValue);
    if (isNaN(dateObj)) return dateValue;
    const day   = String(dateObj.getUTCDate()).padStart(2, '0');
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
    const year  = dateObj.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };
  // ================= TABLE 21 (TÂCHES) =================
  const TABLE21_COLUMNS = [
    "Date", "De", "Description", "Commentaires", "Statut", "Numero tache",
  ];
  const getTable21ColWidth = (col) =>
    col === "Date"          ? "50px"  :
    col === "De"            ? "50px"  :
    col === "Description"   ? "200px" :
    col === "Statut"        ? "80px"  :
    col === "Numero tache"  ? "40px"  :
    col === "Commentaires"  ? "300px" :
    "150px";
  const renderCommentaire = (text) => {
    if (!text || !text.includes("Commentaire :")) return text;
    const [before, after] = text.split("Commentaire :");
    return (
      <>
        <span>{before}</span>
        <span style={{ color: "red", fontWeight: "bold" }}>
          Commentaire : {after}
        </span>
      </>
    );
  };
  // ================= RECALCUL STOCK PROJETÉ =================
  const recalculateProjectedStock = (updatedSimuValues, data) => {
    const newProjected = [];
    for (let i = 0; i < data.length; i++) {
      const prevStock = i === 0
        ? parseFloat(data[0]['stock projete']) || 0
        : newProjected[i - 1];
      const simu         = parseFloat(updatedSimuValues[i - 1]) || 0;
      const livFrn       = parseFloat(data[i]['livraison fournisseur']) || 0;
      const departClient = parseFloat(data[i]['depart client']) || 0;
      const newVal = prevStock + simu + livFrn - departClient;
      newProjected.push(i === 0 ? prevStock : newVal);
    }
    setProjectedStockValues(newProjected);
  };
  // ================= CHARGEMENT DES DONNÉES =================
  const loadTableData = async (source, tableName) => {
    try {
      let jsonData = [];
      if (source === 'analyse_delai_approvisionnement_pg') {
        const response = await fetch(`${API}/analyse_delai_approvisionnement`);
        const rawData = await response.json();
        jsonData = rawData.map(({ ID, Id, Client_id, ...rest }) => rest);
      }
      else if (source === 'commande_a_10_jours_frn_api') {
        const response = await fetch(`${API}/commande_a_10_jours_frn`);
        const rawData = await response.json();
        jsonData = rawData.map(({ ID, client_id, Client_id, ...rest }) => rest);
      }
      else if (source === 'recalage_cmde_frn_api') {
        const response = await fetch(`${API}/recalage_cmde_frn`);
        const rawData = await response.json();
        let cleanedData = rawData.map(({ Id, Client_id, ...rest }) => rest);
        cleanedData = cleanedData.map(row => {
          if (row["Report proposé"] === "31/12/2050") {
            return { ...row, "Report proposé": "ANNULATION" };
          }
          return row;
        });
        cleanedData.sort((a, b) => {
          if (!a.Item) return 1;
          if (!b.Item) return -1;
          return a.Item.localeCompare(b.Item, 'fr', { sensitivity: 'base' });
        });
        jsonData = cleanedData;
      }
      else if (source === 'commande_frn_recalage_stock_fort_api') {
        const response = await fetch(`${API}/commande_frn_recalage_stock_fort`);
        const rawData = await response.json();
        jsonData = rawData.map(({ Id, ID, Client_id, client_id, ...rest }) => rest);
      }
      else if (source === 'commandes_frn_sans_ar_api') {
        const response = await fetch(`${API}/commandes_frn_sans_ar`);
        const rawData = await response.json();
        jsonData = rawData.map(({ ID, Client_id, Age, client_id, ...rest }) => {
          if (rest['Date de commande']) {
            const dateObj = new Date(rest['Date de commande']);
            if (!isNaN(dateObj)) {
              const day   = String(dateObj.getDate()).padStart(2, '0');
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const year  = dateObj.getFullYear();
              rest['Date de commande'] = `${day}/${month}/${year}`;
            }
          }
          return rest;
        });
      }
      else if (source === 'taches_pg') {
        const response = await fetch(`${API}/taches`);
        const rawData = await response.json();
        let cleanedData = rawData.map(
          ({ Id, id, client_id, Client_id, created_at, updated_at, ...rest }) => rest
        );
  jsonData = cleanedData
  .filter(row =>
    row.Département === "APPRO" &&
    (row.Statut === "ouvert" || row.Statut === "en cours")
  )
  .sort((a, b) => new Date(a['Date'] || 0) - new Date(b['Date'] || 0));
      }
      else {
        const response = await fetch(`/${source}`);
        jsonData = await response.json();
      }
      setData(jsonData);
      setActiveTable(tableName);
      setFilteredData([]);
      setHistogramData([]);
      setSimuCmdeFrnValues({});
      setProjectedStockValues([]);
      setRecalageCmds([]);
      setGranularity('mois');
      setShowDetailModal(false);
      setShowCharts(false);
      setItemClickError(null);
    } catch (error) {
      console.error('Erreur chargement données', error);
    }
  };
  // ================= FERMETURE MODAL DÉTAIL ITEM =================
  const closeDetailModal = () => {
    setFilteredData([]);
    setHistogramData([]);
    setSimuCmdeFrnValues({});
    setProjectedStockValues([]);
    setStockMiniValue(null);
    setRecalageCmds([]);
    setGranularity('mois');
    setShowDetailModal(false);
    setItemClickError(null);
  };
  // ================= CLIC SUR ITEM (Card16) =================
  // Normalise une référence Item pour comparer deux tables qui peuvent avoir des
  // écarts d'espaces ou de casse (ex. " ABC123 " vs "abc123") sans être identiques.
  const normalizeItem = (v) => (v ?? '').toString().trim().toUpperCase();
  const handleItemClick = async (value) => {
    const cleanValue = normalizeItem(value);
    setItemClickError(null);
    // La modal s'ouvre tout de suite avec ce qu'on a déjà (le code Item) — elle ne
    // doit jamais rester fermée parce qu'une seule des trois sources a un souci.
    // Chaque section se complète ensuite indépendamment au fur et à mesure.
    setActiveTable('table2');
    setShowDetailModal(true);
    setHistogramData([]);
    setFilteredData([{ Item: value }]);
    setRecalageCmds([]);
    setStockMiniValue(null);

    let filteredHistogram = [];
    try {
      const responseHistogram = await fetch(`${API}/donnee_histogramme`);
      const rawDataHistogram = await responseHistogram.json();
      const cleanDataHistogram = rawDataHistogram.map(
        ({ Id, Client_id, client_id, ...rest }) => rest
      );
      filteredHistogram = cleanDataHistogram
        .filter(row => normalizeItem(row.Item) === cleanValue)
        .sort((a, b) => (parseLocalDate(a.date) || 0) - (parseLocalDate(b.date) || 0));
      setHistogramData(filteredHistogram);
    } catch (err) {
      console.error('Erreur chargement donnee_histogramme', err);
    }

    const firstNegativeRow = filteredHistogram.find(
      row => parseFloat(row['stock projete']) < 0
    );
    const dateRuptureCalculee = firstNegativeRow
      ? formatDate(firstNegativeRow.date)
      : "";

    try {
      const responseItem = await fetch(`${API}/base_de_donne_item`);
      const rawDataItem = await responseItem.json();
      const cleanDataItem = rawDataItem.map(
        ({ ID, Client_id, client_id, ...rest }) => rest
      );
      const filteredItem = cleanDataItem
        .filter(row => normalizeItem(row.Item) === cleanValue)
        .map(row => ({
          ...row,
          "Date de livraison": formatDate(row["Date de livraison"]),
          "Date de rupture": dateRuptureCalculee,
        }));
      if (filteredItem.length > 0) {
        setFilteredData(filteredItem);
        setStockMiniValue(
          filteredItem[0]['Stock mini'] !== undefined ? parseFloat(filteredItem[0]['Stock mini']) : null
        );
      } else {
        // Item absent de base_de_donne_item : la fiche reste affichée (avec le code
        // Item et le reste des données disponibles), simplement signalée.
        setItemClickError(
          `Item "${value}" introuvable dans la base article (base_de_donne_item) — fiche affichée avec les seules données disponibles.`
        );
      }
    } catch (err) {
      console.error('Erreur chargement base_de_donne_item PG', err);
      setItemClickError(`Erreur lors du chargement de la fiche article pour "${value}".`);
    }

    // Commandes fournisseur déjà recalées pour cet item (toutes, y compris ANNULATION,
    // pour alimenter à la fois le tableau de recalage et la courbe simulée du graphe)
    try {
      const responseRecalage = await fetch(`${API}/recalage_cmde_frn`);
      const rawRecalage = await responseRecalage.json();
      const cleanRecalage = rawRecalage.map(({ Id, Client_id, ...rest }) => rest);
      setRecalageCmds(cleanRecalage.filter(row => normalizeItem(row.Item) === cleanValue));
    } catch (err) {
      console.error('Erreur chargement recalage_cmde_frn', err);
      setRecalageCmds([]);
    }
  };
  const filterDataByDepartment = (department) => {
    const filtered = data.filter(row => row.Departement === department);
    setFilteredData(filtered);
  };
  const COLUMN_RENAMES = {
    commande_a_10_jours_frn: {
      "Id": null,
      "Numero de commande": "Numéro de commande",
      "Numero de ligne": "N° de ligne",
      "Description": "Déscription"
    },
    recalage_cmde_frn: {
      "Numero de commande": "Numéro de commande",
      "Numero de ligne": "N° de ligne",
      "Description": "Déscription"
    },
    commandes_frn_sans_ar: {
      "Numero de commande": "Numéro de commande",
      "Description": "Déscription"
    },
    analyse_delai_approvisionnement: {
      "Id": null,
      "Description": "Déscription",
      "Qté liv 10 dernière commande": "Qté 10 dernières livraisons"
    }
  };
  const formatColumn = (tableKey, colName) => {
    const mapping = COLUMN_RENAMES[tableKey];
    if (!mapping) return colName;
    if (mapping[colName] === null) return null;
    return mapping[colName] || colName;
  };
  // ================= GRAPHE STOCK PROJETÉ =================
  // Courbe "Stock projeté" — toujours la valeur réelle / actuelle, indépendante de
  // toute simulation (ni saisie manuelle, ni recalage proposé), pour rester une
  // référence stable pendant que l'utilisateur teste des scénarios.
  const baseChartData = useMemo(() => {
    if (!histogramData || histogramData.length === 0) return null;
    return buildChartData(histogramData, granularity);
  }, [histogramData, granularity]);
  // Lignes "historique" recalculées avec la simulation manuelle (Simu cmde Frn) déjà
  // appliquée — alimente le tableau et la courbe "votre simulation" du graphe.
  const manuallyAdjustedRows = useMemo(() => {
    if (!histogramData || histogramData.length === 0) return histogramData;
    if (!projectedStockValues || projectedStockValues.length === 0) return histogramData;
    return histogramData.map((row, idx) => ({
      ...row,
      'stock projete': projectedStockValues[idx] !== undefined ? projectedStockValues[idx] : row['stock projete'],
    }));
  }, [histogramData, projectedStockValues]);
  // Courbe "après reports/annulations" — simulation automatique des commandes déjà
  // recalées pour cet item, indépendante de la simulation manuelle ci-dessous.
  const simulatedRows = useMemo(() => {
    if (!histogramData || histogramData.length === 0 || recalageCmds.length === 0) return null;
    return buildSimulatedRows(histogramData, recalageCmds);
  }, [histogramData, recalageCmds]);
  // Graphe final : courbe réelle + (si recalage proposé) courbe verte + (si l'utilisateur
  // a saisi une quantité dans "Simu cmde Frn") courbe violette de sa propre simulation.
  const activeChartData = useMemo(() => {
    if (!baseChartData) return null;
    const baseAgg = aggregateRows(histogramData, granularity);
    const datasets = [...baseChartData.datasets];

    if (simulatedRows) {
      const simuAgg = aggregateRows(simulatedRows, granularity);
      const simuMap = {};
      simuAgg.forEach(r => { simuMap[r._periodKey] = parseFloat(r['stock projete']) || 0; });
      const simuDataAligned = baseAgg.map(r => {
        const stockBase = parseFloat(r['stock projete']) || 0;
        const stockSimu = simuMap[r._periodKey] ?? stockBase;
        return Math.round(stockSimu);
      });
      datasets.push({
        label: 'Stock projeté (après reports/annulations)',
        type: 'line',
        data: simuDataAligned,
        borderColor: T2_THEME.ok.fg,
        backgroundColor: 'rgba(30,142,90,0.12)',
        borderWidth: 2.5,
        borderDash: [6, 3],
        fill: false,
        tension: 0.3,
        datalabels: { display: false },
      });
    }

    if (projectedStockValues && projectedStockValues.length > 0) {
      const manualAgg = aggregateRows(manuallyAdjustedRows, granularity);
      const manualMap = {};
      manualAgg.forEach(r => { manualMap[r._periodKey] = parseFloat(r['stock projete']) || 0; });
      const manualDataAligned = baseAgg.map(r => {
        const stockBase = parseFloat(r['stock projete']) || 0;
        const stockManual = manualMap[r._periodKey] ?? stockBase;
        return Math.round(stockManual);
      });
      datasets.push({
        label: 'Stock projeté (votre simulation)',
        type: 'line',
        data: manualDataAligned,
        borderColor: '#7C3AED',
        backgroundColor: 'rgba(124,58,237,0.12)',
        borderWidth: 2.5,
        borderDash: [2, 2],
        fill: false,
        tension: 0.3,
        datalabels: { display: false },
      });
    }

    return { ...baseChartData, datasets };
  }, [baseChartData, histogramData, granularity, simulatedRows, manuallyAdjustedRows, projectedStockValues]);
  // ================= STATUT FIN DE PÉRIODE (bandeau modal) =================
  // Dernière valeur de "stock projeté" :
  //   1) simulation manuelle (Simu cmde Frn) si l'utilisateur a saisi quelque chose
  //   2) sinon simulation automatique du recalage déjà proposé, si elle existe
  //   3) sinon projection brute
  //   - positive  -> rupture temporaire : un recalage de commande fournisseur suffit
  //   - négative/nulle -> il faut passer une nouvelle commande fournisseur
  const getProjectedStockStatus = () => {
    if (!histogramData || histogramData.length === 0) return null;
    const lastIndex = histogramData.length - 1;
    const rawLast = parseFloat(histogramData[lastIndex]['stock projete']);
    let lastValue;
    if (projectedStockValues[lastIndex] !== undefined) {
      lastValue = parseFloat(projectedStockValues[lastIndex]);
    } else if (simulatedRows && simulatedRows.length > 0) {
      lastValue = parseFloat(simulatedRows[simulatedRows.length - 1]['stock projete']);
    } else {
      lastValue = rawLast;
    }
    if (isNaN(lastValue)) return null;
    if (lastValue > 0) {
      return {
        level: 'warning',
        label: 'Rupture temporaire',
        message: "Le stock projeté redevient positif en fin de période : un recalage de commande fournisseur suffit, pas besoin de passer une nouvelle commande.",
      };
    }
    return {
      level: 'danger',
      label: 'Nouvelle commande nécessaire',
      message: "Le stock projeté reste négatif (ou nul) en fin de période : une nouvelle commande fournisseur est nécessaire.",
    };
  };
  const projectedStockStatus = getProjectedStockStatus();
  // ================= RENDER =================
  return (
    <div className="approvisionnement-container">
      {/* Bandeau titre */}
      <div style={{
        position: 'absolute', top: '25px', left: '160px',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <div style={{
          width: '3px', height: '18px',
          background: 'linear-gradient(180deg, #04202d, #38bdf8)',
          borderRadius: '2px',
        }} />
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: '20px', fontWeight: '200',
          letterSpacing: '0.12em', textTransform: 'uppercase', color: '#04202d',
        }}>
          Approvisionnement
        </span>
      </div>
      {/* Cards */}
      <div className="cards-container">
        <div className="card1" style={{ position: 'absolute', top: '100px', left: '330px' }}
          onClick={() => loadTableData('commande_a_10_jours_frn_api', 'table1')}>
          <Card15 />
        </div>
        <div className="card1" style={{ position: 'absolute', top: '100px', left: '585px' }}
          onClick={() => loadTableData('commande_frn_recalage_stock_fort_api', 'table2')}>
          <Card16 />
        </div>
        <div className="card1" style={{ position: 'absolute', top: '100px', left: '840px' }}
          onClick={() => loadTableData('recalage_cmde_frn_api', 'table3')}>
          <Card18 />
        </div>
        <div className="card1" style={{ position: 'absolute', top: '100px', left: '1095px' }}
          onClick={() => loadTableData('commandes_frn_sans_ar_api', 'table4')}>
          <Card19 />
        </div>
        <div className="card1" style={{ position: 'absolute', top: '100px', left: '1350px' }}
          onClick={() => loadTableData('analyse_delai_approvisionnement_pg', 'table20')}>
          <Card20 />
        </div>
        <div className="card1" style={{ position: 'absolute', top: '100px', left: '1605px' }}
          onClick={() => loadTableData('taches_pg', 'table21')}>
          <Card21 />
        </div>
      </div>
      {/* ===== TABLE 1 (Card15) ===== */}
      {activeTable === 'table1' && (
        <div className="table-container1">
          <table>
            <thead>
              <tr>
                {data.length > 0 &&
                  Object.keys(data[0])
                    .filter(col => col !== "Id")
                    .map(col => {
                      if (col === "Numero de commande") return "Numéro de commande";
                      if (col === "Numero de ligne")    return "N° de ligne";
                      if (col === "Description")        return "Déscription";
                      if (col === "Date de livraison prévue") return "Livraison prévue";
                      return col;
                    })
                    .map((col, index) => <th key={index}>{col}</th>)
                }
              </tr>
            </thead>
            <tbody>
              {data
                .sort((a, b) =>
                  new Date(a["Date de livraison prévue"]) - new Date(b["Date de livraison prévue"])
                )
                .map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Object.entries(row)
                      .filter(([colName]) => colName !== "Id")
                      .map(([colName, value], colIndex) => {
                        if (colName === "Horizon") {
                          const meta = getT2StatusMeta(value);
                          return (
                            <td key={colIndex}>
                              <span style={t2PillStyle(meta)}>{value}</span>
                            </td>
                          );
                        }
                        const displayValue = colName.includes("Date") ? formatDate(value) : value;
                        return <td key={colIndex}>{displayValue}</td>;
                      })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {/* ===== TABLE 2 (Card16) — même style que les autres tableaux ===== */}
      {activeTable === 'table2' && (
        <div className="table-container-recalage">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <button onClick={closeDetailModal} style={t2ResetBtnStyle}>
              Réinitialiser
            </button>
          </div>
          <table>
            <thead>
              <tr>
                {data.length > 0 && Object.keys(data[0]).map((col, index) => (
                  <th key={index}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => {
                const meta = getT2StatusMeta(row["Date"]);
                return (
                  <tr key={rowIndex} style={{ borderLeft: `3px solid ${meta.fg}` }}>
                    {Object.entries(row).map(([colName, value], colIndex) => {
                      if (colName === "Item") {
                        return (
                          <td
                            key={colIndex}
                            onClick={() => handleItemClick(value)}
                            style={{ color: T2_THEME.blue, fontWeight: 600, cursor: 'pointer' }}
                          >
                            {value}
                          </td>
                        );
                      }
                      if (colName === "Date") {
                        return (
                          <td key={colIndex}>
                            <span style={t2PillStyle(meta)}>{value}</span>
                          </td>
                        );
                      }
                      return <td key={colIndex}>{value}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* ===== MODAL DÉTAIL ITEM (infos / stocks / historique) ===== */}
      {/* Le scroll se fait sur myssa-modal-content lui-même (style inline,
          exactement comme la modale qui fonctionne dans Stockprojete.jsx) :
          tout le contenu — header compris — défile en un seul bloc, le header
          restant épinglé en haut grâce à position: sticky. */}
      {showDetailModal && (
        <div className="myssa-modal-overlay" onClick={closeDetailModal}>
          <div
            className="myssa-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: MODAL_CONFIG.maxWidth,
              maxHeight: `${MODAL_CONFIG.maxHeightVh}vh`,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <div className="myssa-modal-header">
              <div className="myssa-modal-title">
                <span className="myssa-modal-title-bar" />
                Fiche article{filteredData[0]?.Item ? ` — ${filteredData[0].Item}` : ''}
              </div>
              <button className="myssa-modal-close" onClick={closeDetailModal} aria-label="Fermer">
                ×
              </button>
            </div>
            {itemClickError && (
              <div className="myssa-modal-banner myssa-modal-banner-warning">
                <span className="myssa-modal-banner-dot" />
                <span className="myssa-modal-banner-label">Donnée incomplète</span>
                <span className="myssa-modal-banner-sep">·</span>
                <span className="myssa-modal-banner-message">{itemClickError}</span>
              </div>
            )}
            {projectedStockStatus && (
              <div className={`myssa-modal-banner myssa-modal-banner-${projectedStockStatus.level}`}>
                <span className="myssa-modal-banner-dot" />
                <span className="myssa-modal-banner-label">{projectedStockStatus.label}</span>
                <span className="myssa-modal-banner-sep">·</span>
                <span className="myssa-modal-banner-message">{projectedStockStatus.message}</span>
              </div>
            )}
            <div className="myssa-modal-body">
              <div className="myssa-modal-row">
                {/* Tableau 1 : infos item */}
                <div className="myssa-modal-table-card myssa-modal-table-item">
                  <div className="myssa-modal-card-title">Détails article</div>
                  <table>
                    <tbody>
                      {[
                        "Item",
                        "Description",
                        "Délai d'approvisionnement (jours ouvrés)",
                        "Délais réel",
                        "Date de livraison",
                        "Date de rupture"
                      ].map((col, colIndex) => (
                        <tr key={colIndex}>
                          <td className="myssa-modal-row-label">{col}</td>
                          {filteredData.map((row, rowIndex) => {
                            let cellValue = row[col];
                            let cellStyle = {};
                            if (col === "Statut appro") {
                              if (cellValue === "rupture")            cellStyle.backgroundColor = T2_THEME.j.bg;
                              else if (cellValue === "rupture sèche") cellStyle.backgroundColor = T2_THEME.retard.bg;
                            }
                            if (col === "Date de livraison" && cellValue) {
                              cellStyle.backgroundColor = T2_THEME.ok.bg;
                            }
                            if (col === "Date de rupture" && cellValue) {
                              cellStyle.backgroundColor = T2_THEME.retard.bg;
                            }
                            if (col === "Délais réel") {
                              cellValue = cellValue != null ? Math.round(Number(cellValue)) : cellValue;
                            }
                            return <td key={rowIndex} style={cellStyle}>{cellValue}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Tableau 2 : stocks */}
                <div className="myssa-modal-table-card myssa-modal-table-stock">
                  <div className="myssa-modal-card-title">Stocks</div>
                  <table>
                    <tbody>
                      {["Stock", "Cmde client", "Cmde fournisseur", "Stock projeté", "Stock mini"].map((col, colIndex) => (
                        <tr key={colIndex}>
                          <td className="myssa-modal-row-label">{col}</td>
                          {filteredData.map((row, rowIndex) => {
                            let cellValue = row[col];
                            let cellStyle = {};
                            if (col === "Stock projeté") {
                              if (cellValue > 0)      cellStyle.backgroundColor = T2_THEME.ok.bg;
                              else if (cellValue < 0) cellStyle.backgroundColor = T2_THEME.retard.bg;
                            }
                            if (["Stock", "Cmde client", "Cmde fournisseur", "Stock projeté", "Stock mini"].includes(col)) {
                              cellValue = cellValue != null && cellValue !== ""
                                ? Math.round(Number(cellValue)) : cellValue;
                            }
                            return <td key={rowIndex} style={cellStyle}>{cellValue}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Tableau historique */}
              {histogramData.length > 0 && (
                <div className="myssa-modal-table-card myssa-modal-table-histogram">
                  <div className="myssa-modal-card-title">Historique &amp; projection</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Type de donnée</th>
                        {histogramData.map((row, index) => (
                          <th key={index}>{formatDate(row.date)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {["livraison fournisseur", "depart client", "stock projete", "Simu cmde Frn"].map((type, typeIndex) => (
                        <tr key={typeIndex}>
                          <td className="myssa-modal-row-label"><strong>{type}</strong></td>
                          {histogramData.map((row, index) => {
                            if (type === "stock projete") {
                              const projected = projectedStockValues[index] !== undefined
                                ? projectedStockValues[index]
                                : row["stock projete"];
                              const bgColor =
                                projected < 0
                                  ? T2_THEME.retard.bg
                                  : stockMiniValue !== null && projected <= stockMiniValue
                                  ? T2_THEME.j.bg
                                  : T2_THEME.ok.bg;
                              return (
                                <td key={index} style={{ backgroundColor: bgColor }}>
                                  {typeof projected === 'number'
                                    ? Math.round(projected)
                                    : Math.round(Number(projected))}
                                </td>
                              );
                            } else if (type === "Simu cmde Frn") {
                              return (
                                <td key={index}>
                                  <input
                                    type="number"
                                    value={simuCmdeFrnValues[index] || ''}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      const updatedSimu = { ...simuCmdeFrnValues, [index]: newValue };
                                      setSimuCmdeFrnValues(updatedSimu);
                                      recalculateProjectedStock(updatedSimu, histogramData);
                                    }}
                                    style={{ width: '80px' }}
                                  />
                                </td>
                              );
                            } else {
                              return (
                                <td key={index}>
                                  {row[type] != null ? Math.round(Number(row[type])) : row[type]}
                                </td>
                              );
                            }
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Commandes fournisseur — décalage possible (même format que Stockprojete.jsx) */}
              {recalageCmds.length > 0 && (
                <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                    ⟳ Commandes fournisseur — décalage possible pour réduire le stock fort
                  </div>
                  {recalageCmds.some(c => (c.statut || '').trim().toUpperCase() !== 'ANNULATION') && (
                    <div style={{ fontSize: 11, color: '#1E40AF', background: '#DBEAFE', borderRadius: 6, padding: '5px 10px', marginBottom: 10 }}>
                      La courbe verte en pointillés représente le stock projeté après application des reports et annulations proposés.
                    </div>
                  )}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#DBEAFE' }}>
                          {['Fournisseur', 'N° commande', 'Date livraison', 'Qté', 'Report proposé', 'Statut', 'Approvisionneur'].map(h => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#1E40AF', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {recalageCmds.map((c, i) => {
                          const statutLabel = getStockprojeteStatutLabel(c);
                          const isAnnulation = (c.statut || '').trim().toUpperCase() === 'ANNULATION';
                          return (
                            <tr key={i} style={{ borderBottom: '0.5px solid #BFDBFE', background: i % 2 === 0 ? '#F0F7FF' : '#FFFFFF', opacity: isAnnulation ? 0.75 : 1 }}>
                              <td style={{ padding: '6px 10px', fontWeight: 500, color: '#1E40AF' }}>{c.Fournisseur}</td>
                              <td style={{ padding: '6px 10px', fontFamily: 'IBM Plex Mono, monospace', color: '#374151' }}>{c['Numero de commande']}</td>
                              <td style={{ padding: '6px 10px', color: '#374151' }}>{formatDate(c['Date de livraison'])}</td>
                              <td style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'right', color: '#0D2140' }}>{fmtQty(c['Quantité commandée'])}</td>
                              <td style={{ padding: '6px 10px', color: isAnnulation ? '#A32D2D' : '#854F0B', fontWeight: 500 }}>
                                {isAnnulation ? <span style={{ color: '#B91C1C', fontWeight: 700 }}>—</span> : formatDate(c['Report proposé'])}
                              </td>
                              <td style={{ padding: '6px 10px' }}>
                                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: statutLabel.bg, color: statutLabel.color, whiteSpace: 'nowrap' }}>
                                  {statutLabel.label}
                                </span>
                              </td>
                              <td style={{ padding: '6px 10px', color: '#6B7280', fontSize: 11 }}>{c.Approvisionneur}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Graphique stock projeté */}
              {histogramData.length > 0 && activeChartData && (
                <div className="myssa-modal-table-card myssa-modal-table-histogram">
                  <div className="myssa-modal-card-title myssa-modal-card-title-row">
                    <span>Évolution du stock</span>
                    <div className="myssa-granularity-group">
                      {[['jour', 'Jour'], ['semaine', 'Semaine'], ['mois', 'Mois']].map(([k, label]) => (
                        <button
                          key={k}
                          onClick={() => setGranularity(k)}
                          className={`myssa-granularity-btn${granularity === k ? ' active' : ''}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="myssa-graph-wrapper">
                    <Line data={activeChartData} options={chartOptions} />
                  </div>
                  {projectedStockValues && projectedStockValues.length > 0 && (
                    <div className="myssa-graph-note">
                      <span className="myssa-graph-note-dot" />
                      La courbe violette en pointillés représente le stock projeté avec vos quantités saisies dans « Simu cmde Frn ».
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <style>{`
            .myssa-modal-overlay {
              position: fixed;
              inset: 0;
              background: rgba(13, 33, 64, 0.55);
              backdrop-filter: blur(2px);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000;
              padding: 24px;
              animation: myssa-fade-in 0.15s ease;
            }
            @keyframes myssa-fade-in {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
            .myssa-modal-content {
              background: #FFFFFF;
              border-radius: 14px;
              width: 100%;
              box-shadow: 0 18px 50px rgba(13, 33, 64, 0.35);
              scrollbar-width: thin;
              scrollbar-color: #C7D2E0 #F6F9FC;
            }
            .myssa-modal-content::-webkit-scrollbar {
              width: 10px;
            }
            .myssa-modal-content::-webkit-scrollbar-track {
              background: #F6F9FC;
            }
            .myssa-modal-content::-webkit-scrollbar-thumb {
              background: #C7D2E0;
              border-radius: 8px;
              border: 2px solid #F6F9FC;
            }
            .myssa-modal-content::-webkit-scrollbar-thumb:hover {
              background: #A8B6CA;
            }
            .myssa-modal-banner {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 10px 20px;
              font-size: 12.5px;
              line-height: 1.4;
              border-bottom: 1px solid ${T2_THEME.border};
            }
            .myssa-modal-banner-dot {
              width: 8px;
              height: 8px;
              border-radius: 50%;
              flex-shrink: 0;
            }
            .myssa-modal-banner-warning {
              background: ${T2_THEME.j.bg};
              color: ${T2_THEME.j.fg};
            }
            .myssa-modal-banner-warning .myssa-modal-banner-dot {
              background: ${T2_THEME.j.fg};
            }
            .myssa-modal-banner-danger {
              background: ${T2_THEME.retard.bg};
              color: ${T2_THEME.retard.fg};
            }
            .myssa-modal-banner-danger .myssa-modal-banner-dot {
              background: ${T2_THEME.retard.fg};
            }
            .myssa-modal-banner-label {
              font-weight: 700;
              text-transform: uppercase;
              font-size: 11px;
              letter-spacing: 0.03em;
              white-space: nowrap;
            }
            .myssa-modal-banner-sep {
              opacity: 0.5;
            }
            .myssa-modal-banner-message {
              flex: 1;
            }
            .myssa-modal-header {
              position: sticky;
              top: 0;
              z-index: 5;
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 14px 20px;
              background: #FFFFFF;
              border-bottom: 1px solid #E3E9F0;
            }
            .myssa-modal-title {
              display: flex;
              align-items: center;
              gap: 10px;
              color: #0D2140;
              font-family: 'IBM Plex Mono', monospace;
              font-size: 13px;
              letter-spacing: 0.04em;
              text-transform: uppercase;
              font-weight: 500;
            }
            .myssa-modal-title-bar {
              width: 3px;
              height: 14px;
              background: linear-gradient(180deg, #0D2140, #2BAADB);
              border-radius: 2px;
            }
            .myssa-modal-close {
              background: #FFFFFF;
              border: 1px solid #E3E9F0;
              color: #0D2140;
              width: 30px;
              height: 30px;
              border-radius: 8px;
              font-size: 18px;
              line-height: 1;
              cursor: pointer;
              transition: background 0.15s ease;
            }
            .myssa-modal-close:hover {
              background: #F6F9FC;
            }
            .myssa-modal-body {
              padding: 20px;
              display: flex;
              flex-direction: column;
              gap: 18px;
            }
            .myssa-modal-row {
              display: flex;
              gap: 18px;
              flex-wrap: wrap;
            }
            .myssa-modal-table-card {
              background: #FFFFFF;
              border: 1px solid #E3E9F0;
              border-radius: 12px;
              box-shadow: 0 2px 10px rgba(13, 33, 64, 0.05);
              overflow: hidden;
            }
            .myssa-modal-table-item  { flex: 1 1 360px; }
            .myssa-modal-table-stock { flex: 1 1 280px; }
            .myssa-modal-table-histogram {
              width: 100%;
              overflow-x: auto;
            }
            .myssa-modal-card-title-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
            }
            .myssa-granularity-group {
              display: flex;
              gap: 6px;
            }
            .myssa-granularity-btn {
              background: #FFFFFF;
              color: #64748B;
              border: 1px solid #E3E9F0;
              padding: 3px 11px;
              border-radius: 999px;
              font-size: 11px;
              font-family: 'IBM Plex Sans', sans-serif;
              font-weight: 500;
              cursor: pointer;
              text-transform: none;
              letter-spacing: normal;
            }
            .myssa-granularity-btn.active {
              background: #0D2140;
              border-color: #0D2140;
              color: #FFFFFF;
            }
            .myssa-graph-wrapper {
              padding: 16px;
              height: 340px;
            }
            .myssa-graph-note {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 8px 16px 14px 16px;
              font-size: 11.5px;
              color: #6D28D9;
              line-height: 1.4;
            }
            .myssa-graph-note-dot {
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #7C3AED;
              flex-shrink: 0;
            }
            .myssa-modal-card-title {
              font-family: 'IBM Plex Mono', monospace;
              font-size: 11px;
              font-weight: 500;
              letter-spacing: 0.06em;
              text-transform: uppercase;
              color: #64748B;
              background: #F6F9FC;
              padding: 9px 16px;
              border-bottom: 1px solid #E3E9F0;
            }
            .myssa-modal-table-card table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            .myssa-modal-table-histogram table {
              min-width: max-content;
            }
            .myssa-modal-table-card th {
              background: #FBFCFE;
              color: #64748B;
              font-family: 'IBM Plex Mono', monospace;
              font-size: 10.5px;
              font-weight: 600;
              letter-spacing: 0.03em;
              text-transform: uppercase;
              text-align: center;
              padding: 8px 14px;
              border-bottom: 1px solid #E3E9F0;
              white-space: nowrap;
            }
            .myssa-modal-table-card td {
              padding: 9px 14px;
              text-align: center;
              color: #16243B;
              border-bottom: 1px solid #F0F4F8;
              white-space: nowrap;
            }
            .myssa-modal-table-card tbody tr:last-child td {
              border-bottom: none;
            }
            .myssa-modal-table-card tbody tr:nth-child(even) {
              background: #FBFCFE;
            }
            .myssa-modal-row-label {
              text-align: left !important;
              font-family: 'IBM Plex Mono', monospace;
              font-size: 11.5px;
              color: #8B95A6;
              white-space: nowrap;
            }
          `}</style>
        </div>
      )}
      {/* ===== TABLE filteredTable ===== */}
      {activeTable === 'filteredTable' && (
        <div className="table-container-filtered">
          <table>
            <thead>
              <tr>
                {filteredData.length > 0 && Object.keys(filteredData[0]).map((col, index) => (
                  <th key={index}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {Object.values(row).map((value, colIndex) => (
                    <td key={colIndex}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* ===== TABLE 17 ===== */}
      {activeTable === 'table17' && (
        <div className="table-container1">
          <table>
            <thead>
              <tr>
                {data.length > 0 && Object.keys(data[0]).map((col, index) => (
                  <th key={index}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {Object.values(row).map((value, colIndex) => (
                    <td key={colIndex}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* ===== TABLE 3 (Card18) ===== */}
      {activeTable === 'table3' && (
        <div className="table-container1">
          <table>
            <thead>
              <tr>
                {data.length > 0 &&
                  Object.keys(data[0])
                    .filter(col => col !== 'ID' && col !== 'Id')
                    .map(col => {
                      if (col === "Numero de commande") return "Numéro de commande";
                      if (col === "Numero de ligne")    return "N° de ligne";
                      if (col === "Description")        return "Déscription";
                      return col;
                    })
                    .map((col, index) => <th key={index}>{col}</th>)
                }
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => {
                const estAnnulation   = String(row["statut"] ?? "").trim().toUpperCase() === "ANNULATION";
                const estInf2Semaines = Object.values(row).some(
                  v => String(v ?? "").trim().toLowerCase() === "inf +2 semaines"
                );
                return (
                  <tr
                    key={rowIndex}
                    style={
                      estAnnulation    ? { borderLeft: `3px solid ${T2_THEME.retard.fg}` } :
                      estInf2Semaines  ? { borderLeft: `3px solid ${T2_THEME.j.fg}` } :
                      {}
                    }
                  >
                    {Object.entries(row)
                      .filter(([colName]) => colName !== 'ID' && colName !== 'Id')
                      .map(([colName, value], colIndex) => {
                        let displayValue = value;
                        let cellStyle    = {};
                        if (colName === "Date de livraison" || colName === "Report proposé") {
                          displayValue = formatDate(value);
                        }
                        if (String(value ?? "").trim().toUpperCase() === "ANNULATION") {
                          cellStyle = { color: T2_THEME.retard.fg, fontWeight: "700" };
                        }
                        if (String(value ?? "").trim().toLowerCase() === "inf +2 semaines") {
                          cellStyle = { color: T2_THEME.j.fg, fontWeight: "700" };
                        }
                        return (
                          <td key={colIndex} style={cellStyle}>
                            {displayValue}
                          </td>
                        );
                      })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* ===== TABLE 4 (Card19) ===== */}
      {activeTable === 'table4' && (
        <div className="table-container1">
          <table>
            <thead>
              <tr>
                {data.length > 0 &&
                  Object.keys(data[0])
                    .filter(col => col !== 'ID' && col !== 'Client_id')
                    .map(col => {
                      if (col === "Numero de commande") return "Numéro de commande";
                      if (col === "Description")        return "Déscription";
                      return col;
                    })
                    .map((col, index) => <th key={index}>{col}</th>)
                }
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {Object.entries(row)
                    .filter(([colName]) => colName !== 'ID' && colName !== 'Client_id')
                    .map(([colName, value], colIndex) => {
                      if (colName === 'Statut AR' && value === 'Manquant') {
                        return (
                          <td key={colIndex}>
                            <span style={t2PillStyle(T2_THEME.retard)}>{value}</span>
                          </td>
                        );
                      }
                      return <td key={colIndex}>{value}</td>;
                    })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* ===== TABLE 20 (Card20 — Analyse délai approvisionnement) ===== */}
      {activeTable === 'table20' && (
        <div className="table-container-table20">
          <table>
            {/* colgroup : largeurs pilotées par COLUMN_WIDTHS_TABLE20 */}
            <colgroup>
              {data.length > 0 && Object.keys(data[0]).map((col, i) => (
                <col key={i} style={{ width: COLUMN_WIDTHS_TABLE20[col] || TABLE20_DEFAULT_WIDTH }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {data.length > 0 && Object.keys(data[0]).map((col, index) => {
                  const className = col === "Item" ? "item-col" : col === "Description" ? "description-col" : "";
                  return <th key={index} className={className}>{col}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {data
                .filter(row => row["Délais proposé"] !== "pas de changement proposé")
                .map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Object.entries(row).map(([colName, value], colIndex) => {
                      let cellClass = "";
                      if ((colName === "Ecart" || colName === "Evolution") && !isNaN(value)) {
                        const numericValue = parseFloat(value);
                        cellClass = numericValue < 0 ? "negative" : "positive";
                      }
                      const colClass =
                        colName === "Item"        ? "item-col" :
                        colName === "Description" ? "description-col" : "";
                      return (
                        <td key={colIndex} className={`${cellClass} ${colClass}`}>
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {/* ===== TABLE 21 (Card21 — Tâches) ===== */}
      {activeTable === 'table21' && (
        <div className="table-container-table21">
          <table>
            <thead>
              <tr>
                {TABLE21_COLUMNS.map((col, idx) => (
                  <th key={idx} style={{ width: getTable21ColWidth(col) }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {TABLE21_COLUMNS.map((col, colIndex) => {
                    let value = row[col];
                    if (col === "Date") value = formatDate(value);
                    if (col === "Commentaires") {
                      return (
                        <td key={colIndex}>
                          <textarea
                            value={value || ''}
                            onChange={(e) => {
                              const newData = [...data];
                              newData[rowIndex][col] = e.target.value;
                              setData(newData);
                            }}
                            onBlur={async () => {
                              const commentaire = data[rowIndex]['Commentaires'] || '';
                              const statut      = data[rowIndex]['Statut'] || '';
                              const numeroTache = row['Numero tache'];
                              try {
                                await fetch(`${API}/taches/update`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ numero_tache: numeroTache, commentaire, statut }),
                                });
                              } catch (err) { console.error('Erreur sauvegarde PG :', err); }
                              if (numeroTache.startsWith('A')) {
                                try {
                                  await fetch(`${API}/base_commentaire_animation/update`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ numero_tache: numeroTache, commentaire }),
                                  });
                                } catch (err) { console.error('Erreur base_commentaire_animation :', err); }
                              }
                            }}
                            placeholder="Ajouter un commentaire"
                            style={{ width: '100%', minHeight: '60px', resize: 'vertical' }}
                          />
                        </td>
                      );
                    }
                    if (col === "Statut") {
                      const getStatutClass = (val) =>
                        val === 'ouvert'   ? 'statut-ouvert'  :
                        val === 'en cours' ? 'statut-encours' :
                        val === 'soldé'    ? 'statut-solde'   : '';
                      return (
                        <td key={colIndex} className={getStatutClass(value)}>
                          <select
                            value={value}
                            onChange={async (e) => {
                              const newStatut   = e.target.value;
                              const newData     = [...data];
                              newData[rowIndex][col] = newStatut;
                              setData(newData);
                              const commentaire = newData[rowIndex]['Commentaires'] || '';
                              const numeroTache = row['Numero tache'];
                              try {
                                await fetch(`${API}/taches/update`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ numero_tache: numeroTache, commentaire, statut: newStatut }),
                                });
                              } catch (err) { console.error('Erreur sauvegarde PG :', err); }
                              if (numeroTache.startsWith('A')) {
                                try {
                                  await fetch(`${API}/base_commentaire_animation/update`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ numero_tache: numeroTache, commentaire }),
                                  });
                                } catch (err) { console.error('Erreur base_commentaire_animation :', err); }
                              }
                            }}
                          >
                            <option value="ouvert">ouvert</option>
                            <option value="en cours">en cours</option>
                            <option value="soldé">soldé</option>
                          </select>
                        </td>
                      );
                    }
                    if (col === "Description") {
                      return (
                        <td key={colIndex} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {value && value.includes("Commentaire :") ? renderCommentaire(value) : value}
                        </td>
                      );
                    }
                    return <td key={colIndex}>{value}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Graphiques */}
      {showCharts && (
        <div className="charts-wrapper">
          <div className="camembert-container">
            <Camembert3 />
          </div>
          <div className="graph2-container">
            <Graph2 />
          </div>
        </div>
      )}
    </div>
  );
};
export default Approvisionnement;